// Every Excel import goes through a preview (PLAN §2C): new / changed / unchanged / errors, with reasons, before saving.
import { useState, type ReactNode } from "react";
import { FileUp, FileDown } from "lucide-react";
import { readWorkbook, pickFile, download, writeWorkbook, type Sheet, type OutSheet } from "@/lib/xlsx";
import { Badge, Button, Modal, errMsg, useToast } from "./ui";

export interface PreviewRow {
  line: number;
  key: string;
  status: "new" | "changed" | "unchanged" | "error" | "warning";
  detail?: string;
}

export interface Preview {
  rows: PreviewRow[];
  /** File-level problems (missing headers etc.). */
  problems: string[];
  /** Extra inputs or notes shown above the table. */
  note?: ReactNode;
  commit: () => Promise<unknown>;
  /** Commit is blocked while any row has an error. Default true. */
  strict?: boolean;
}

export function ImportButton({ label = "Import", build, disabled }: { label?: string; build: (sheets: Sheet[], fileName: string) => Preview | Promise<Preview>; disabled?: boolean }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const toast = useToast();
  const open = async () => {
    const f = await pickFile(".xlsx");
    if (!f) return;
    try {
      const sheets = await readWorkbook(await f.arrayBuffer());
      setPreview(await build(sheets, f.name));
    } catch (e) {
      toast({ text: `Couldn't read ${f.name}: ${errMsg(e)}`, tone: "error" });
    }
  };
  return (
    <>
      <Button onClick={open} disabled={disabled}>
        <FileUp className="size-4" /> {label}
      </Button>
      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </>
  );
}

/** Step 2 of every import: what will change (new / changed / unchanged / errors), then commit. */
export function PreviewModal({ preview, onClose, onBack }: { preview: Preview; onClose: () => void; onBack?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<"all" | PreviewRow["status"]>("all");
  const toast = useToast();
  const counts = { new: 0, changed: 0, unchanged: 0, error: 0, warning: 0 };
  for (const r of preview.rows) counts[r.status]++;
  const blocked = (preview.strict ?? true) && (counts.error > 0 || preview.problems.length > 0);
  const shown = preview.rows.filter((r) => filter === "all" || r.status === filter);
  return (
    <Modal
      open
      onClose={onClose}
      title="Import preview"
      wide="xl"
      footer={
        <>
          {onBack && (
            <Button className="mr-auto" onClick={onBack}>
              Back to columns
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={blocked || counts.new + counts.changed + counts.warning === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await preview.commit();
                toast({ text: `Imported ${counts.new} new, ${counts.changed} changed`, tone: "success" });
                onClose();
              } catch (e) {
                toast({ text: errMsg(e), tone: "error" });
              } finally {
                setBusy(false);
              }
            }}
          >
            {blocked ? "Fix errors to import" : `Import ${counts.new + counts.changed + counts.warning} of ${preview.rows.length} rows`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {preview.note}
        {preview.problems.length > 0 && (
          <ul className="space-y-1 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {preview.problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}
        {/* Every row lands in exactly one bucket, so the buckets add up to the file's row count (Cesar found "new 26,
            unchanged 2, Import 27" hard to square). Only new, changed and warning rows are written. */}
        <p className="text-sm text-slate-600">
          {preview.rows.length} rows in the file:{" "}
          {[
            counts.new && `${counts.new} new`,
            counts.changed && `${counts.changed} changed`,
            counts.warning && `${counts.warning} with warnings (imported)`,
            counts.unchanged && `${counts.unchanged} already the same (nothing to do)`,
            counts.error && `${counts.error} with errors (skipped)`,
          ]
            .filter(Boolean)
            .join(", ")}
          .
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          {(["all", "new", "changed", "warning", "unchanged", "error"] as const)
            .filter((k) => k === "all" || counts[k] > 0)
            .map((k) => (
              <button key={k} onClick={() => setFilter(k)} className={`rounded border px-2 py-0.5 ${filter === k ? "border-brand-500 bg-brand-50" : "border-slate-200"}`}>
                {k === "all" ? `All ${preview.rows.length}` : `${k[0].toUpperCase() + k.slice(1)} ${counts[k]}`}
              </button>
            ))}
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="w-16 py-1">Row</th>
              <th className="py-1">Key</th>
              <th className="w-24 py-1">Status</th>
              <th className="py-1">Detail</th>
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 500).map((r, i) => (
              <tr key={i} className="border-t border-slate-100 align-top">
                <td className="py-1 text-slate-500">{r.line}</td>
                <td className="py-1 font-medium">{r.key}</td>
                <td className="py-1">
                  <Badge tone={r.status === "error" ? "red" : r.status === "new" ? "green" : r.status === "changed" ? "blue" : r.status === "warning" ? "amber" : "slate"}>{r.status}</Badge>
                </td>
                <td className="whitespace-pre-line py-1 text-slate-600">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length > 500 && <p className="text-xs text-slate-500">Showing the first 500 of {shown.length}.</p>}
      </div>
    </Modal>
  );
}

export function ExportButton({ label = "Export", file, sheets }: { label?: string; file: string; sheets: () => OutSheet[] | Promise<OutSheet[]> }) {
  const toast = useToast();
  return (
    <Button
      onClick={async () => {
        try {
          download(await writeWorkbook(await sheets()), file);
        } catch (e) {
          toast({ text: errMsg(e), tone: "error" });
        }
      }}
    >
      <FileDown className="size-4" /> {label}
    </Button>
  );
}
